import { Fragment } from 'react';
import { Info, MoveDown, MoveUp } from 'lucide-react';
import type { SalesPerformance } from '@/types/sales-performance.types';
import { count, money, percent, relativeChange } from './format';

/** Figma Kim-AI 6570:63755 — the Overview KPI row. */

const deltaFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });

function Delta({ current, previous }: { current: number | null; previous: number | null }) {
  const change = current === null || previous === null ? null : relativeChange(current, previous);
  if (change === null) return <span className='sr-only'>No comparison</span>;
  const value = `${deltaFormat.format(Math.abs(change))}%`;
  // The design gives a rise the compact tag and a fall the wider "vs prev" tag.
  return change >= 0
    ? <span title='Change from the previous equal-length period' className='flex shrink-0 items-center gap-1 rounded-2xl bg-green-100 px-2 py-1 text-xs leading-4 text-green-500'><MoveUp size={12} aria-hidden />{value}</span>
    : <span title='Change from the previous equal-length period' className='flex shrink-0 items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs leading-4 text-red-500'><MoveDown size={12} aria-hidden />{value} vs prev</span>;
}

function Label({ text, help }: { text: string; help: string }) {
  return <span className='flex items-center gap-1.5'>
    <span className='text-[13px] font-medium leading-[18px] whitespace-nowrap text-ink'>{text}</span>
    <span title={help} tabIndex={0} aria-label={help} className='flex size-3.5 shrink-0'><Info size={14} className='text-gray-800' aria-hidden /></span>
  </span>;
}

/** The dashed rule between "Buying intent" and "No intent". */
function DashedRule() {
  return <svg aria-hidden className='block h-px w-0 min-w-full'>
    <line x1='0.5' y1='0.5' x2='100%' y2='0.5' stroke='var(--color-gray-400)' strokeLinecap='round' strokeDasharray='1.93 3.86' />
  </svg>;
}

export function PerformanceOverview({ report }: { report: SalesPerformance }) {
  const { overview: o, previous: p, currency } = report;
  const cards = [
    { label: 'Revenue', value: money(o.revenue, currency), current: Number(o.revenue), previous: Number(p.revenue), gap: 'gap-1', help: 'Total qualifying order value before returns, including tax and shipping after discounts. Each order is counted once.' },
    { label: 'Conversion rate', value: percent(o.conversionRate), current: o.conversionRate, previous: p.conversionRate, gap: 'gap-1', help: 'Attributed orders ÷ conversations with buying intent. Repeat purchases can make this exceed 100%.' },
    { label: 'Total order', value: count(o.orders), current: o.orders, previous: p.orders, gap: 'gap-3', help: 'Distinct attributed orders for this conversation cohort and currency.' },
    { label: 'Avg. order value', value: money(o.averageOrderValue, currency), current: o.averageOrderValue === null ? null : Number(o.averageOrderValue), previous: p.averageOrderValue === null ? null : Number(p.averageOrderValue), gap: 'gap-1', help: 'Attributed revenue ÷ attributed orders.' },
  ];
  return <section aria-label='Overview' className='flex flex-col gap-3 bg-white'>
    <h2 className='text-xs leading-[15px] font-medium text-gray-900'>Overview</h2>
    <div className='flex flex-col gap-3 lg:flex-row lg:items-center'>
      <div className='flex h-28 w-full shrink-0 items-center justify-between rounded-3xl border border-gray-500 bg-white p-4 lg:w-72'>
        <div className='flex h-full flex-col justify-between'>
          <Label text='Sales chats' help='Conversations created in the selected period.' />
          <strong className='text-xl font-semibold leading-7 text-ink-strong'>{count(o.conversations)}</strong>
        </div>
        <dl className='flex h-full flex-col justify-between'>
          <div className='flex items-center gap-1 whitespace-nowrap'><dd className='text-base leading-6 text-ink'>{count(o.withIntent)}</dd><dt className='text-xs leading-4 text-gray-700'>Buying intent</dt></div>
          <DashedRule />
          <div className='flex items-center gap-1 whitespace-nowrap'><dd className='text-base leading-6 text-ink'>{count(o.withoutIntent)}</dd><dt className='text-xs leading-4 text-gray-700'>No intent</dt></div>
        </dl>
      </div>
      <div className='min-w-0 flex-1 rounded-3xl border border-gray-500 p-[15px]'>
        <div className='flex flex-wrap gap-6 md:h-20 md:flex-nowrap'>
          {cards.map((card, index) => <Fragment key={card.label}>
            {index > 0 && <span aria-hidden className='hidden w-px shrink-0 bg-gray-400 md:block' />}
            <div className='flex min-w-[140px] flex-1 flex-col justify-between gap-3'>
              <Label text={card.label} help={card.help} />
              <div className={`flex items-center ${card.gap}`}>
                <strong className='text-xl font-semibold leading-7 whitespace-nowrap text-ink-strong'>{card.value}</strong>
                <Delta current={card.current} previous={card.previous} />
              </div>
            </div>
          </Fragment>)}
        </div>
      </div>
    </div>
  </section>;
}
